/* eslint-disable complexity */
import { Button, Typography, withStyles, WithStyles, Tooltip, Grid, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { equals } from 'ramda';
import { useCallback, memo, PropsWithChildren, Fragment, useState } from 'react';
import { useSelector } from 'react-redux';

import ackIcon from '~assets/images/icons/ack.svg';
import { getAlarmCode } from '~features/alarms/utils';
import { getFACPColor } from '~styles/colors';

import { AcknowledgedByButton } from './AcknowledgedByButton';
import { styles } from './AlarmListCard.styles';
import { GeoInfoDialog } from './UnitInfoDialog/UnitGeoInfoDialog';
import { UnitImagesListDialog } from './UnitInfoDialog/UnitImagesListDialog';
import { UnitsInfoButton } from './UnitsInfoButton';
import {
  getAlarmMeta,
  getAlarmUnitLabel,
  getAlarmUnitLabelField,
  getInfoDialogsPermission,
  isFullAlarm,
  getFACPColorLabel,
} from './utils';

import { ProtectedContent } from '~components';
import { appDetailRoutes } from '~constants';
import { Alarm, AlarmManualAck, PermissionName } from '~models';
import { selectors } from '~store';
import {
  Card,
  CardDetails,
  CardSummary,
  ContentField,
  IconText,
  ContentWrapper,
  Divider,
  NavLink,
  CardColumn,
} from '~ui-kit';
import { formatDate } from '~utils';

export interface AlarmListCardCallbacks {
  onSilence?(alarm: Alarm): void;
  setAlarm?(alarm: Alarm): void;
  onAlarmsCount?(alarmCount: number, alarm: Alarm): void;
  setDialog?(): void;
  onExpand(alarm: Alarm, expanded: boolean): void;
  getDate?(alarm: Alarm): number;
  count?: number;
  showTooltip?: boolean;
}

export type AlarmListCardProps = PropsWithChildren<WithStyles<typeof styles>> &
  AlarmListCardCallbacks & {
    alarm: Alarm;
    columns?: string[];
    backTo?: string;
    expanded?: boolean;
    timeFormat: string;
  };

export const AlarmListCard = memo(
  withStyles(styles)(
    ({
      alarm,
      columns = [],
      backTo,
      onExpand,
      onSilence = () => {},
      setAlarm = () => {},
      onAlarmsCount = () => {},
      setDialog = () => {},
      getDate = (alarm: Alarm) => alarm.createdAt,
      count = 0,
      showTooltip = false,
      classes,
      expanded,
      timeFormat,
    }: AlarmListCardProps) => {
      if (!alarm) {
        return null;
      }

      const { status, title, icon } = getAlarmMeta(alarm.alarmType);
      const isExpanded = expanded ?? alarm.meta?.isExpanded;
      const unitLabel = getAlarmUnitLabel(alarm);
      const timeZone = useSelector(selectors.profile.getUserTimeZone, equals);
      const isSelfMonitoring = useSelector(selectors.app.isSelfMonitoring, equals);
      const isHighContrast = useSelector(selectors.app.isHighContrast, equals);
      const isAdminUser = useSelector(selectors.auth.isAdminUser, equals);
      const imageviewPermission = useSelector(selectors.auth.hasPermissions(
        PermissionName.DASHBOARD_IMAGE_VIEW), equals);
      const theme = useTheme();
      const facpColor = getFACPColor(theme, alarm.facpColor);
      const facpColorLabel = getFACPColorLabel(alarm.facpColor);
      const displayAcknowledgedByButton = isSelfMonitoring && isAdminUser;
      const displayViewImagesButton = isSelfMonitoring && imageviewPermission &&
        (alarm.unitType === 'HYBRID' || alarm.unitType === 'SUBSCRIBER');
      const [open, setOpen] = useState(false);
      const [openImage, setOpenImage] = useState(false);

      const alarmTitle = (
        <IconText
          label={alarm.alarmTypeDescription}
          icon={icon}
          status={status}
          classes={{ root: clsx(classes.titleRoot), title: clsx(classes.title) }}
        />
      );
      const isAcknowledgeAllBtn = isFullAlarm(alarm);

      const onAckCallback = useCallback(() => {
        setAlarm(alarm);
      }, [alarm]);

      const onSilenceCallback = useCallback(() => {
        onSilence(alarm);
      }, [alarm]);

      const onAckAllCallback = useCallback(() => {
        onAlarmsCount(count, alarm);
        setDialog();
      }, [count, alarm]);

      const onResolveCallback = useCallback(() => {
        setDialog();
        setAlarm(alarm);
      }, [alarm]);

      const onExpandCallback = useCallback(
        (e: unknown, panelExpanded: boolean) => {
          if (expanded) {
            return;
          }

          onExpand(alarm, panelExpanded);
        },
        [alarm, isExpanded]
      );

      const getBtnStyle = (disabled: boolean, type?) => {
        if (disabled) {
          return {};
        }

        if (type) {
          return { background: facpColor };
        }

        return {
          borderColor: facpColor,
          color: facpColor,
        };
      };

      if (alarm.isTest && alarm.manualAck === AlarmManualAck.ACKNOWLEDGED) {
        return null;
      }

      return (
        <Card
          key={alarm.id}
          expandable
          expanded={isExpanded}
          onChange={onExpandCallback}
          classes={{ root: clsx(classes.root) }}
          TransitionProps={{ timeout: 0, unmountOnExit: true, in: isExpanded }}
        >
          <CardSummary
            status={status}
            expandable
            hideIcon={expanded}
            classes={{
              cardSummaryRoot: clsx(classes.cardSummary, facpColorLabel, { 'high-contrast': isHighContrast }),
            }}
          >
            {showTooltip ? (
              <Tooltip title={alarm.alarmTypeDescription} placement="right">
                {alarmTitle}
              </Tooltip>
            ) : (
              alarmTitle
            )}

            <Divider flexItem orientation="vertical" spacing="left" />

            <ContentWrapper classes={{ root: classes.fieldsWrapper }}>
              <ContentField title={unitLabel} width={60} classes={{ root: classes.field }}>
                {getAlarmUnitLabelField(alarm, backTo)}
              </ContentField>
              <Divider flexItem orientation="vertical" spacing="right" />

              {count ? (
                <Fragment>
                  <CardColumn value="count" columns={columns}>
                    <ContentField
                      width={40}
                      title="Alarms count"
                      classes={{ root: clsx(classes.field, classes.count) }}
                    >
                      {count}
                    </ContentField>
                  </CardColumn>

                  <Divider flexItem orientation="vertical" spacing="right" />
                </Fragment>
              ) : null}

              <ContentField title="Address" classes={{ root: classes.address }}>
                <Tooltip title={<span>{alarm?.address1 || ''}<br />{alarm.address2 || ''}</span>}>
                  <Grid>{alarm?.address1 || 'N/A'}</Grid>
                </Tooltip>
              </ContentField>

              {isSelfMonitoring && (
                <Fragment>
                  <Divider flexItem orientation="vertical" spacing="right" />

                  <ContentField title="Description" classes={{ root: classes.address }}>
                    <Tooltip title={alarm?.facpDescription ?? ''}>
                      <Grid>{alarm?.facpDescription || 'N/A'}</Grid>
                    </Tooltip>
                  </ContentField>
                </Fragment>
              )}
            </ContentWrapper>

            <CardColumn columns={columns} value="actions">
              <ContentWrapper spacing="both" classes={{ root: clsx(classes.actions, { expanded }) }}>
                <div className={classes.buttons}>
                  {isAcknowledgeAllBtn && (
                    <ProtectedContent permissions={[PermissionName.DASHBOARD_ACKNOWLEDGE_ALL]}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classes.btnAckAll}
                        disabled={alarm.meta?.isLoading || count === 1}
                        style={getBtnStyle(alarm.meta?.isLoading || count === 1)}
                        onClick={onAckAllCallback}
                      >
                        Ack All
                      </Button>
                    </ProtectedContent>
                  )}

                  <Divider flexItem orientation="vertical" spacing="left" />
                  <CardColumn columns={columns} value="silence">
                    <ProtectedContent permissions={[PermissionName.DASHBOARD_SILENCE]}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={alarm.silent || alarm.meta?.isLoading}
                        style={getBtnStyle(Boolean(alarm.silent || alarm.meta?.isLoading))}
                        onClick={onSilenceCallback}
                      >
                        Silence
                      </Button>
                    </ProtectedContent>
                  </CardColumn>

                  <ProtectedContent permissions={[PermissionName.DASHBOARD_ACKNOWLEDGE]}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={alarm.meta?.isLoading}
                      style={getBtnStyle(Boolean(alarm.meta?.isLoading), 'ack')}
                      onClick={onAckCallback}
                    >
                      Ack
                    </Button>
                  </ProtectedContent>
                </div>
              </ContentWrapper>
            </CardColumn>

            <CardColumn columns={columns} value="acknowledged">
              <Divider flexItem orientation="vertical" spacing="right" />

              {isSelfMonitoring ? (
                <ContentWrapper classes={{ root: classes.resolveBtn }}>
                  <Button variant="contained" color="primary" size="small" onClick={onResolveCallback}>
                    Resolve
                  </Button>
                </ContentWrapper>
              ) : (
                <ContentWrapper classes={{ root: classes.acknowledged }}>
                  <img src={ackIcon} className="icon" />
                  <Typography variant="body1">Acknowledged</Typography>
                </ContentWrapper>
              )}
            </CardColumn>
          </CardSummary>

          <CardDetails
            classes={{ root: clsx(classes.cardDetails, facpColorLabel, { 'high-contrast': isHighContrast }) }}
            status={status}
          >
            <ContentField title="Date & Time" width={200}>
              <Tooltip title={formatDate(getDate(alarm), false, timeFormat, timeZone) as string}>
                <Grid>{formatDate(getDate(alarm), false, timeFormat, timeZone)}</Grid>
              </Tooltip>
            </ContentField>

            <ProtectedContent permissions={getInfoDialogsPermission(alarm)}>
              <ContentWrapper classes={{ root: classes.notes }}>
                {alarm.unitType !== 'MNR' && (
                  <UnitsInfoButton
                    unitType={alarm.unitType}
                    unitId={alarm.unitId}
                    businessUnitId={alarm.businessUnitId}
                    title="Notes"
                    backTo={backTo}
                    buttonStyle={getBtnStyle(Boolean(alarm.meta?.isLoading))}
                  />
                )}
              </ContentWrapper>
            </ProtectedContent>
            <ProtectedContent permissions={getInfoDialogsPermission(alarm)}>
              <ContentWrapper classes={{ root: classes.customerInfo }}>
                {alarm.unitType !== 'MNR' && (
                  <UnitsInfoButton
                    unitType={alarm.unitType}
                    unitId={alarm.unitId}
                    businessUnitId={alarm.businessUnitId}
                    alarm={alarm}
                    title="Contact Info"
                    backTo={backTo}
                    buttonStyle={getBtnStyle(Boolean(alarm.meta?.isLoading))}
                  />
                )}
              </ContentWrapper>
            </ProtectedContent>

            <ContentField title="Code" width={180}>
              {getAlarmCode(alarm).trim()}
            </ContentField>
            <ContentField title="Business Unit" width={250}>
              <NavLink to={appDetailRoutes.businessUnitDetails(alarm.businessUnitId, backTo)}>
                <Tooltip title={alarm.businessUnitName}>
                  <Grid>{alarm.businessUnitName}</Grid>
                </Tooltip>
              </NavLink>
            </ContentField>
            <ContentField title="Alarm type" width={150}>
              {title}
            </ContentField>
            <CardColumn columns={columns} value="unacknowledged">
              {alarm.unitType !== 'MNR' && (
                <Fragment>
                  <Button
                    variant="outlined"
                    color="primary"
                    style={getBtnStyle(Boolean(alarm.meta?.isLoading))}
                    size="small"
                    onClick={() => setOpen(true)}
                  >
                    Geo Data
                  </Button>

                  <GeoInfoDialog open={open} alarm={alarm} onClose={() => setOpen(false)} />
                </Fragment>
              )}
            </CardColumn>

            {displayViewImagesButton && (
              <CardColumn columns={columns} value="images">
                <ContentWrapper spacing="left">
                  <Button
                    variant="outlined"
                    color="primary"
                    style={getBtnStyle(Boolean(alarm.meta?.isLoading))}
                    size="small"
                    onClick={() => setOpenImage(true)}
                  >
                    View Images
                  </Button>

                  <UnitImagesListDialog
                    open={openImage}
                    alarm={alarm}
                    onClose={() => setOpenImage(false)}
                  />
                </ContentWrapper>
              </CardColumn>
            )}

            {displayAcknowledgedByButton && alarm.acknowledgedBy > 0 && (
              <CardColumn columns={columns} value="acknowledged">
                <ContentWrapper classes={{ root: classes.resolveBtn }}>
                  <AcknowledgedByButton
                    id={(alarm.id as unknown) as number}
                    dateTime={formatDate(alarm.acknowledgedAt, false, timeFormat, timeZone) as string}
                    backTo={backTo}
                  />
                </ContentWrapper>
              </CardColumn>
            )}
          </CardDetails>
        </Card>
      );
    }
  )
);
